import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useSiteSettings } from '@/hooks/useSiteSettings';

const LANGS = [
  { code: 'zh', flag: 'https://flagcdn.com/w20/cn.png', label: '中文' },
  { code: 'en', flag: 'https://flagcdn.com/w20/us.png', label: 'English' },
  { code: 'ru', flag: 'https://flagcdn.com/w20/ru.png', label: 'Русский' },
];

const SERVICE_LINKS = [
  { href: '/services', labelKey: 'nav_service_overview' },
  { href: '/ivf', labelKey: 'nav_service_ivf' },
  { href: '/surrogacy', labelKey: 'nav_service_surrogacy' },
  { href: '/fertility', labelKey: 'nav_service_full' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const { logo_url } = useSiteSettings();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [mobileServiceOpen, setMobileServiceOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language || 'zh');
  const serviceRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (serviceRef.current && !serviceRef.current.contains(e.target as Node)) {
        setServiceOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileServiceOpen(false);
  }, [location.pathname]);

  // Listen for i18n language change
  useEffect(() => {
    const handleLangChange = (lng: string) => setCurrentLang(lng);
    i18n.on('languageChanged', handleLangChange);
    return () => { i18n.off('languageChanged', handleLangChange); };
  }, []);

  // Update html lang attribute
  useEffect(() => {
    const langMap: Record<string, string> = { zh: 'zh-CN', en: 'en', ru: 'ru' };
    document.documentElement.lang = langMap[currentLang] || 'zh-CN';
  }, [currentLang]);

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setCurrentLang(code);
    setLangOpen(false);
  };

  const navLinks = [
    { href: '/', label: t('nav_home'), hasDropdown: false },
    { href: '/services', label: t('nav_services'), hasDropdown: true },
    { href: '/cases', label: t('nav_cases'), hasDropdown: false },
    { href: '/news', label: t('nav_knowledge'), hasDropdown: false },
    { href: '/contact', label: t('nav_contact'), hasDropdown: false },
  ];

  const isServicesActive = location.pathname.startsWith('/services') || location.pathname === '/surrogacy' || location.pathname === '/ivf' || location.pathname === '/fertility';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-10 h-10 flex items-center justify-center overflow-hidden rounded-lg">
              <img src={logo_url} alt="RTB Logo" className="w-full h-full object-contain" />
            </div>
            <span className="font-bold text-sm md:text-base leading-tight hidden sm:block text-[#1a3a5c]">
              吉尔吉斯斯坦<br />{t('brand_sub')}
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.href} className="relative" ref={serviceRef}>
                  <button
                    onClick={() => setServiceOpen(!serviceOpen)}
                    className={`flex items-center gap-0.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isServicesActive
                        ? 'text-[#c9a96e]'
                        : 'text-[#1a3a5c] hover:text-[#c9a96e]'
                    }`}
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line text-base transition-transform ${serviceOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {serviceOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl border border-gray-100 overflow-hidden min-w-[160px] py-1">
                      {SERVICE_LINKS.map((s) => (
                        <Link
                          key={s.href}
                          to={s.href}
                          onClick={() => setServiceOpen(false)}
                          className={`block px-4 py-2.5 text-sm hover:bg-[#f5f0e8] transition-colors cursor-pointer whitespace-nowrap ${
                            location.pathname === s.href ? 'text-[#c9a96e] font-medium' : 'text-[#1a3a5c]'
                          }`}
                        >
                          {t(s.labelKey)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    (location.pathname === link.href || (link.href === '/news' && (location.pathname.startsWith('/news') || location.pathname.startsWith('/tags'))))
                      ? 'text-[#c9a96e]'
                      : 'text-[#1a3a5c] hover:text-[#c9a96e]'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Language switcher - globe icon + dropdown */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap text-[#1a3a5c] hover:bg-gray-100"
                title={t('nav_switch_lang')}
              >
                <i className="ri-global-line text-sm"></i>
                <span className="uppercase">{currentLang === 'zh' ? 'ZH' : currentLang === 'en' ? 'EN' : 'RU'}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white rounded-xl border border-gray-100 overflow-hidden min-w-[140px] py-1 shadow-lg">
                  {LANGS.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLang(lang.code)}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        currentLang === lang.code
                          ? 'bg-[#f5f0e8] text-[#c9a96e] font-medium'
                          : 'text-[#1a3a5c] hover:bg-gray-50'
                      }`}
                    >
                      <img src={lang.flag} alt={lang.label} className="w-5 h-4 rounded-sm object-cover" />
                      <span>{lang.label}</span>
                      {currentLang === lang.code && (
                        <i className="ri-check-line text-[#c9a96e] ml-auto text-sm"></i>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Button - desktop only */}
            <Link
              to="/contact"
              className="hidden md:flex items-center gap-1 bg-[#c9a96e] hover:bg-[#b8944f] text-white text-sm font-medium px-4 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-customer-service-line"></i>
              {t('nav_consult')}
            </Link>

            {/* Mobile menu toggle */}
            <button
              className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer rounded-lg transition-colors text-[#1a3a5c] hover:bg-gray-100"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu - full screen overlay style */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex flex-col gap-1">
              {/* Home */}
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer flex items-center justify-between ${
                  location.pathname === '/' ? 'bg-[#f5f0e8] text-[#c9a96e]' : 'text-[#1a3a5c] hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-home-4-line text-base w-5 text-center"></i>
                  {t('nav_home')}
                </span>
              </Link>

              {/* Services with submenu */}
              <div>
                <button
                  onClick={() => setMobileServiceOpen(!mobileServiceOpen)}
                  className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer flex items-center justify-between ${
                    isServicesActive ? 'bg-[#f5f0e8] text-[#c9a96e]' : 'text-[#1a3a5c] hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-stethoscope-line text-base w-5 text-center"></i>
                    {t('nav_services')}
                  </span>
                  <i className={`ri-arrow-down-s-line text-base transition-transform ${mobileServiceOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileServiceOpen && (
                  <div className="ml-4 mt-1 border-l-2 border-[#c9a96e]/20 pl-4 flex flex-col gap-1">
                    {SERVICE_LINKS.map((s) => (
                      <Link
                        key={s.href}
                        to={s.href}
                        onClick={() => setMobileOpen(false)}
                        className={`px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                          location.pathname === s.href
                            ? 'text-[#c9a96e] font-medium bg-[#f5f0e8]'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-[#1a3a5c]'
                        }`}
                      >
                        {t(s.labelKey)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Cases */}
              <Link
                to="/cases"
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname === '/cases' ? 'bg-[#f5f0e8] text-[#c9a96e]' : 'text-[#1a3a5c] hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-heart-line text-base w-5 text-center"></i>
                  {t('nav_cases')}
                </span>
              </Link>

              {/* Knowledge */}
              <Link
                to="/news"
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname.startsWith('/news') ? 'bg-[#f5f0e8] text-[#c9a96e]' : 'text-[#1a3a5c] hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-book-open-line text-base w-5 text-center"></i>
                  {t('nav_knowledge')}
                </span>
              </Link>

              {/* Contact */}
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname === '/contact' ? 'bg-[#f5f0e8] text-[#c9a96e]' : 'text-[#1a3a5c] hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-phone-line text-base w-5 text-center"></i>
                  {t('nav_contact')}
                </span>
              </Link>

              {/* CTA */}
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className="mt-2 bg-[#c9a96e] hover:bg-[#b8944f] text-white text-sm font-semibold px-4 py-3.5 rounded-full text-center whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 transition-colors"
              >
                <i className="ri-customer-service-line"></i>
                {t('nav_consult')}
              </Link>

              {/* Lang switcher in mobile */}
              <div className="flex items-center gap-2 pt-2 pb-1">
                {LANGS.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLang(lang.code)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      currentLang === lang.code
                        ? 'bg-[#f5f0e8] text-[#c9a96e]'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <img src={lang.flag} alt={lang.label} className="w-4 h-3 rounded-sm object-cover" />
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}